import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Grid, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, EmptyState } from "../ui";
import { useAuth } from "../../context/AuthContext";
import { resolveAttendanceLocation, LOCATION_REQUIRED_MSG, isUsableAttendanceLocation } from "../../utils/resolveAttendanceLocation";
import { getBreakStatsForRecord, fmtDurFromMs } from "../../utils/attendanceBreakStats";
import {
  DEFAULT_SATURDAY_POLICY,
  isCompanyOffDay,
  localDateKey,
} from "../../utils/weeklyOff";

/* ---------------- helpers ---------------- */

function fmtTime(date, opts = { hour: "2-digit", minute: "2-digit", hour12: true }) {
  if (!date) return "—";
  return new Date(date).toLocaleTimeString("en-IN", opts);
}
function fmtHM(date) {
  if (!date) return "—";
  return new Date(date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtClockHMS(date) {
  return new Date(date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}
function fmtDurHMS(ms) {
  if (!ms || ms < 0) return "00:00:00";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}
function fmtDurHM(ms) {
  if (!ms || ms < 0) return "0h 0m";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

function greet(hour) {
  if (hour < 5) return { text: "Good night", icon: <NightsStayOutlinedIcon /> };
  if (hour < 12) return { text: "Good morning", icon: <WbSunnyOutlinedIcon /> };
  if (hour < 17) return { text: "Good afternoon", icon: <WbSunnyOutlinedIcon /> };
  if (hour < 21) return { text: "Good evening", icon: <WbSunnyOutlinedIcon /> };
  return { text: "Working late", icon: <NightsStayOutlinedIcon /> };
}

function hoursOf(w) {
  if (!w) return 0;
  return (w.hours || 0) + (w.minutes || 0) / 60 + (w.seconds || 0) / 3600;
}

function startOfWeek(d) {
  const day = d.getDay(); // 0 Sun..6 Sat
  const diff = (day + 6) % 7; // Mon as start
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - diff);
  return out;
}

function startOfMonth(d) {
  const out = new Date(d);
  out.setDate(1);
  out.setHours(0, 0, 0, 0);
  return out;
}

/* ---------------- main ---------------- */

const SHIFT_START_HOUR = 9.5;     // 09:30 default
const SHIFT_END_HOUR = 18;        // 18:00
const SHIFT_HOURS = SHIFT_END_HOUR - SHIFT_START_HOUR; // 8.5 calendar span on shift bar
/** Net working hours target for a full day (matches backend half-day / full-day rules). */
const EXPECTED_NET_WORK_HOURS = 9;
const EXPECTED_NET_WORK_MS = EXPECTED_NET_WORK_HOURS * 3600000;
const EARLY_LEAVE_REASON_HOURS = 8;
const EARLY_LEAVE_REASON_MS = EARLY_LEAVE_REASON_HOURS * 3600000;

export default function AttendancePortalNew() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [now, setNow] = useState(new Date());
  const [todayRecord, setTodayRecord] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [canCheckinOffice, setCanCheckinOffice] = useState(true);
  const [reasonOpen, setReasonOpen] = useState(null);
  const [reason, setReason] = useState("");
  const [location, setLocation] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState("");
  // Raw GPS coords captured with the address text — sent to the backend so it
  // can verify the position against configured geo-fences (inside a fence ⇒
  // treated as a normal office punch even when the office IP list is stale).
  const [geoCoords, setGeoCoords] = useState(null);
  const [earlyLeaveReasonOpen, setEarlyLeaveReasonOpen] = useState(false);
  const [earlyLeaveReason, setEarlyLeaveReason] = useState("");
  const [holidays, setHolidays] = useState([]);
  const [satPolicy, setSatPolicy] = useState(DEFAULT_SATURDAY_POLICY);

  const buildOutsidePayload = (reasonText, locationText, coords = geoCoords) => ({
    reason: (reasonText || "").trim(),
    location: (locationText || "").trim(),
    isOutsideOffice: true,
    lat: coords.lat,
    lng: coords.lng,
  });

  const applyResolvedLocation = (loc) => {
    if (isUsableAttendanceLocation(loc)) {
      setLocation(loc.text);
      setGeoCoords(loc.coords);
      setLocationError("");
      return loc;
    }
    setLocation("");
    setGeoCoords(null);
    setLocationError(LOCATION_REQUIRED_MSG);
    return null;
  };

  const fetchOutsideLocation = async () => {
    setLocationLoading(true);
    setLocationError("");
    try {
      const loc = await resolveAttendanceLocation();
      return applyResolvedLocation(loc);
    } catch (_) {
      setLocation("");
      setGeoCoords(null);
      setLocationError(LOCATION_REQUIRED_MSG);
      return null;
    } finally {
      setLocationLoading(false);
    }
  };

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ipRes, recsRes, holRes, shiftRes] = await Promise.all([
        api.get("/api/attendance/can-checkin-from-office").catch(() => ({ data: { canCheckin: true } })),
        api.get("/api/attendance/attendancerecords").catch(() => ({ data: [] })),
        api.get("/api/holidays").catch(() => ({ data: [] })),
        api.get("/api/attendance-adv/my-shift").catch(() => ({ data: {} })),
      ]);
      const canOffice = ipRes.data?.canCheckin ?? ipRes.data?.allowed ?? true;
      setCanCheckinOffice(canOffice);
      const list = Array.isArray(recsRes.data) ? recsRes.data : recsRes.data?.data || [];
      setRecords(list);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todays = list.find((r) => {
        const d = new Date(r.checkInTime || r.createdAt);
        return d >= today;
      });
      setTodayRecord(todays || null);
      const holList = Array.isArray(holRes.data) ? holRes.data : holRes.data?.data || [];
      setHolidays(holList);
      setSatPolicy(shiftRes.data?.saturdayPolicy || DEFAULT_SATURDAY_POLICY);
    } catch (err) {
      toast.error("Could not load attendance");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (!reasonOpen) return;
    let cancelled = false;
    setLocation("");
    setGeoCoords(null);
    setLocationError("");
    setLocationLoading(true);
    resolveAttendanceLocation()
      .then((loc) => {
        if (cancelled) return;
        applyResolvedLocation(loc);
      })
      .catch(() => {
        if (!cancelled) {
          setLocation("");
          setGeoCoords(null);
          setLocationError(LOCATION_REQUIRED_MSG);
        }
      })
      .finally(() => {
        if (!cancelled) setLocationLoading(false);
      });
    return () => { cancelled = true; };
  }, [reasonOpen]);

  const doCheckin = async (reasonText, locationText, coords = geoCoords) => {
    setActionLoading(true);
    try {
      if (!canCheckinOffice && (!coords || !(locationText || "").trim())) {
        toast.error(LOCATION_REQUIRED_MSG);
        return;
      }
      const body = !canCheckinOffice
        ? buildOutsidePayload(reasonText, locationText, coords)
        : {};
      const res = await api.post("/api/attendance/checkin", body);
      const msg = res.data?.msg || res.data?.message;
      toast.success(msg || (res.data?.pending ? "Check-in submitted for manager approval" : "Checked in"));
      setReasonOpen(null); setReason(""); setLocation(""); setGeoCoords(null); setLocationError("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.message || "Check-in failed");
    } finally { setActionLoading(false); }
  };

  const doCheckout = async (reasonText, locationText, earlyReasonText, coords = geoCoords) => {
    setActionLoading(true);
    try {
      if (!canCheckinOffice && (!coords || !(locationText || "").trim())) {
        toast.error(LOCATION_REQUIRED_MSG);
        return;
      }
      const officeEarlyReason = (earlyReasonText || "").trim();
      const body = !canCheckinOffice
        ? buildOutsidePayload(reasonText, locationText, coords)
        : officeEarlyReason
          ? { reason: officeEarlyReason }
          : {};
      const res = await api.post("/api/attendance/checkout", body);
      const msg = res.data?.msg || res.data?.message;
      toast.success(msg || (res.data?.pending ? "Check-out submitted for manager approval" : "Checked out"));
      setReasonOpen(null); setReason(""); setLocation(""); setGeoCoords(null); setLocationError("");
      setEarlyLeaveReasonOpen(false); setEarlyLeaveReason("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.message || "Check-out failed");
    } finally { setActionLoading(false); }
  };

  const handleAction = async (kind) => {
    if (kind === "checkin") {
      if (!canCheckinOffice) { setReasonOpen("checkin"); return; }
      doCheckin();
      return;
    }
    // checkout
    if (!canCheckinOffice) {
      // If they already checked in off-site / WFH, reuse that reason instead of
      // asking for the "outside" reason all over again on checkout — but still
      // require a fresh GPS location at checkout time.
      const priorReason = todayRecord?.checkInReason
        || (todayRecord?.checkInSource === "outside" ? "Work from home" : "");
      if (priorReason) {
        const loc = await fetchOutsideLocation();
        if (!loc) {
          toast.error(LOCATION_REQUIRED_MSG);
          return;
        }
        doCheckout(priorReason, loc.text, undefined, loc.coords);
        return;
      }
      setReasonOpen("checkout");
      return;
    }
    if (workingMs < EARLY_LEAVE_REASON_MS) setEarlyLeaveReasonOpen(true);
    else doCheckout();
  };

  const isCheckedIn = !!(todayRecord && todayRecord.checkInTime && !todayRecord.checkOutTime);
  const isDone = !!(todayRecord && todayRecord.checkOutTime);
  const breakStats = useMemo(
    () => getBreakStatsForRecord(todayRecord, now),
    [todayRecord, now]
  );
  const onBreak = breakStats.onBreak;
  const workingMs = isCheckedIn || isDone ? breakStats.netWorkingMs : 0;

  // Week stats
  const weekData = useMemo(() => {
    const weekStart = startOfWeek(now);
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
    let totalMs = 0;
    const perDay = days.map((d) => {
      const next = new Date(d); next.setDate(next.getDate() + 1);
      const rec = records.find((r) => {
        const rd = new Date(r.checkInTime || r.createdAt);
        return rd >= d && rd < next;
      });
      let hoursToday = 0;
      if (rec) {
        hoursToday = hoursOf(rec.workingHours);
        // If still checked in (today only), extend from checkIn to now
        if (rec.checkInTime && !rec.checkOutTime && d.toDateString() === now.toDateString()) {
          hoursToday = getBreakStatsForRecord(rec, now).netWorkingMs / 3600000;
        }
      }
      totalMs += hoursToday * 3600000;
      return { date: d, hours: hoursToday, record: rec };
    });
    return { days: perDay, totalMs, targetMs: 40 * 3600000 };
  }, [records, now]);

  // Month stats + insights
  const holidayByDate = useMemo(() => {
    const map = {};
    holidays.forEach((h) => {
      const d = new Date(h.date);
      map[localDateKey(d)] = h;
    });
    return map;
  }, [holidays]);

  const monthStats = useMemo(() => {
    const mStart = startOfMonth(now);
    const monthRecords = records.filter((r) => new Date(r.checkInTime || r.createdAt) >= mStart);
    let present = 0, halfDays = 0, late = 0, overtime = 0, totalHours = 0, earliestMin = null, latestMin = null;
    let punctualityEligible = 0;
    monthRecords.forEach((r) => {
      if (!r.checkInTime) return;
      const h = hoursOf(r.workingHours);
      totalHours += h;
      if (r.status === "Half-day") halfDays += 1;
      else if (h >= 4) present += 1;
      const ci = new Date(r.checkInTime);
      const ciMin = ci.getHours() * 60 + ci.getMinutes();
      if (!isCompanyOffDay(ci, { saturdayPolicy: satPolicy, holidayByKey: holidayByDate })) {
        punctualityEligible += 1;
        if (ciMin > SHIFT_START_HOUR * 60 + 15) late += 1;
      }
      if (h > EXPECTED_NET_WORK_HOURS) overtime += (h - EXPECTED_NET_WORK_HOURS);
      earliestMin = earliestMin === null ? ciMin : Math.min(earliestMin, ciMin);
      latestMin = latestMin === null ? ciMin : Math.max(latestMin, ciMin);
    });
    const avgHours = monthRecords.length ? totalHours / monthRecords.length : 0;
    const punctualityPct = punctualityEligible
      ? Math.round(((punctualityEligible - late) / punctualityEligible) * 100)
      : 100;
    return {
      present, halfDays, late, overtime,
      total: monthRecords.length, avgHours,
      punctualityPct,
      earliestMin, latestMin,
    };
  }, [records, now, satPolicy, holidayByDate]);

  // Last 14 days sparkline
  const sparkline = useMemo(() => {
    const out = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const next = new Date(d); next.setDate(next.getDate() + 1);
      const rec = records.find((r) => {
        const rd = new Date(r.checkInTime || r.createdAt);
        return rd >= d && rd < next;
      });
      let h = rec ? hoursOf(rec.workingHours) : 0;
      if (rec && rec.checkInTime && !rec.checkOutTime && d.toDateString() === now.toDateString()) {
        h = getBreakStatsForRecord(rec, now).netWorkingMs / 3600000;
      }
      out.push({ date: d, hours: h });
    }
    return out;
  }, [records, now]);

  // Streak — skip Sundays, 1st/3rd/5th Saturdays, and holidays
  const streak = useMemo(() => {
    let count = 0;
    for (let i = 0; i < 60; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      if (isCompanyOffDay(d, { saturdayPolicy: satPolicy, holidayByKey: holidayByDate })) continue;
      d.setHours(0, 0, 0, 0);
      const next = new Date(d); next.setDate(next.getDate() + 1);
      const rec = records.find((r) => {
        const rd = new Date(r.checkInTime || r.createdAt);
        return rd >= d && rd < next;
      });
      if (rec && hoursOf(rec.workingHours) >= 4) count += 1;
      else if (i === 0) continue; // today without record shouldn't break streak
      else break;
    }
    return count;
  }, [records, now, satPolicy, holidayByDate]);

  // Next holiday
  const nextHoliday = useMemo(() => {
    const future = holidays.filter((h) => new Date(h.date) >= new Date(now.toDateString())).sort((a, b) => new Date(a.date) - new Date(b.date));
    return future[0] || null;
  }, [holidays, now]);

  // Shift progress (0-1)
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const shiftProgress = Math.min(Math.max((nowHour - SHIFT_START_HOUR) / SHIFT_HOURS, 0), 1);

  // Expected clock-out = check-in + target net work + all break time (breaks extend the day, not counted as work)
  const expectedEnd = useMemo(() => {
    if (!todayRecord?.checkInTime) return null;
    const start = new Date(todayRecord.checkInTime);
    return new Date(start.getTime() + EXPECTED_NET_WORK_MS + breakStats.totalBreakMs);
  }, [todayRecord, breakStats.totalBreakMs]);

  const headerIcon = greet(now.getHours()).icon;
  const greeting = greet(now.getHours()).text;
  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <Box p={3}>
      <PageHeader
        title="Attendance"
        subtitle="Your workday at a glance"
        icon={<AccessTimeOutlinedIcon />}
        actions={[
          <Button key="req" variant="outlined" startIcon={<EditCalendarOutlinedIcon />} onClick={() => nav("/AttendanceRegularization")}>
            Request correction
          </Button>,
          <Button key="history" variant="outlined" startIcon={<CalendarMonthOutlinedIcon />} onClick={() => nav("/GetMyAttendance")}>
            Full history
          </Button>,
        ]}
      />

      <Grid container spacing={2.5} mb={3}>
        {/* ============== Hero card with live clock + action ============== */}
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: "linear-gradient(145deg, #1e1b3b 0%, #27234b 45%, #2c2556 100%)",
              color: "white", position: "relative", overflow: "hidden",
              minHeight: 340,
              boxShadow: "0 8px 32px rgba(30,27,59,0.2)",
              border: onBreak ? "2px solid rgba(249,188,6,0.65)" : "2px solid transparent",
            }}
          >
            {/* Ambient status glow */}
            <Box
              sx={{
                position: "absolute", top: -140, right: -140, width: 380, height: 380, borderRadius: "50%",
                background: onBreak
                  ? "radial-gradient(circle, rgba(249,188,6,0.45) 0%, transparent 65%)"
                  : isCheckedIn
                    ? "radial-gradient(circle, rgba(57,198,69,0.35) 0%, transparent 65%)"
                    : isDone
                      ? "radial-gradient(circle, rgba(92,133,255,0.28) 0%, transparent 65%)"
                      : "radial-gradient(circle, rgba(108,80,191,0.38) 0%, transparent 65%)",
                transition: "all 0.6s ease",
              }}
            />
            <Box
              sx={{
                position: "absolute", bottom: -120, left: -80, width: 280, height: 280, borderRadius: "50%",
                background: "radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)",
              }}
            />
            {/* Subtle grid pattern */}
            <Box
              sx={{
                position: "absolute", inset: 0, opacity: 0.04,
                backgroundImage: "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "28px 28px",
                maskImage: "radial-gradient(ellipse at center, rgba(0,0,0,0.4) 30%, transparent 75%)",
              }}
            />

            {/* Status row */}
            <Stack direction="row" alignItems="center" spacing={1.5} mb={2} sx={{ position: "relative" }}>
              <StatusPill
                label={onBreak ? "ON BREAK" : isCheckedIn ? "LIVE SESSION" : isDone ? "COMPLETED" : "NOT CHECKED IN"}
                color={onBreak ? "#F9BC06" : isCheckedIn ? "#39C645" : isDone ? "#5C85FF" : "#F9BC06"}
                pulse={isCheckedIn || onBreak}
              />
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "rgba(255,255,255,0.75)" }}>
                <Box sx={{ fontSize: 18 }}>{headerIcon}</Box>
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                  {greeting} • {now.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                </Typography>
              </Stack>
            </Stack>

            {/* Big timer block */}
            <Box sx={{ position: "relative" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(255,255,255,0.55)", mb: 0.5 }}>
                {onBreak ? "Working time (on break)" : isCheckedIn ? "Working time (excl. breaks)" : isDone ? "Total worked today" : "Current time"}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 48, md: 68 },
                  fontWeight: 800,
                  lineHeight: 1,
                  fontFeatureSettings: '"tnum"',
                  letterSpacing: "-0.02em",
                  background: onBreak
                    ? "linear-gradient(135deg, #F9BC06 0%, #FFD580 100%)"
                    : isCheckedIn
                    ? "linear-gradient(135deg, #39C645 0%, #8CE99A 100%)"
                    : isDone
                      ? "linear-gradient(135deg, #5C85FF 0%, #B4D0FF 100%)"
                      : "linear-gradient(135deg, #A684E8 0%, #E4D7FC 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {isCheckedIn ? fmtDurHMS(workingMs) : isDone ? fmtDurHMS(workingMs) : fmtClockHMS(now)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", mt: 1 }}>
                {onBreak
                  ? <>On break since <Box component="span" sx={{ color: "#FFD580", fontWeight: 700 }}>{fmtTime(breakStats.openBreak?.start || breakStats.breaks.find((b) => b.isOpen)?.start)}</Box> · Break time is not counted in working hours</>
                  : isCheckedIn
                  ? <>Checked in at <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>{fmtTime(todayRecord.checkInTime)}</Box>
                    {breakStats.count > 0 && <> · {breakStats.count} break{breakStats.count > 1 ? "s" : ""} today ({fmtDurFromMs(breakStats.totalBreakMs)} off)</>}
                    {" "}· Hey {firstName}, keep going 👋</>
                  : isDone
                    ? <>You clocked {fmtTime(todayRecord.checkInTime)} → {fmtTime(todayRecord.checkOutTime)} · Well done, {firstName} ✨</>
                    : <>Hey <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>{firstName}</Box>, ready to start your day?</>
                }
              </Typography>
            </Box>

            {/* Action row */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={3} alignItems={{ sm: "center" }} sx={{ position: "relative" }}>
              {isDone ? (
                <Box
                  sx={{
                    px: 2.5, py: 1.25, borderRadius: 2, display: "inline-flex", alignItems: "center", gap: 1,
                    bgcolor: "rgba(92,133,255,0.15)", border: "1px solid rgba(92,133,255,0.4)",
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ color: "#9EBBFF", fontSize: 20 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#E3EAFF" }}>
                    Day completed · See you tomorrow!
                  </Typography>
                </Box>
              ) : (
                <Box
                  component="button"
                  disabled={actionLoading}
                  onClick={() => handleAction(isCheckedIn ? "checkout" : "checkin")}
                  sx={{
                    cursor: actionLoading ? "wait" : "pointer",
                    border: "none", px: 3.5, py: 1.5, borderRadius: 2.5,
                    minWidth: 180, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 1.25,
                    bgcolor: isCheckedIn ? "#39C645" : "#fff",
                    color: isCheckedIn ? "#fff" : "#27234b",
                    fontWeight: 800, fontSize: 15, letterSpacing: 0.3,
                    fontFamily: "inherit",
                    boxShadow: isCheckedIn
                      ? "0 8px 24px rgba(57,198,69,0.4), 0 0 0 1px rgba(57,198,69,0.3)"
                      : "0 8px 24px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1)",
                    transition: "all 0.2s ease",
                    "&:hover:not(:disabled)": {
                      transform: "translateY(-1px)",
                      bgcolor: "#4F67D9",
                      color: "#fff",
                      fontWeight: 800,
                      boxShadow: "0 12px 32px rgba(79,103,217,0.5), 0 0 0 1px rgba(79,103,217,0.45)",
                      "& svg": { color: "#fff" },
                    },
                    "&:disabled": { opacity: 0.6 },
                  }}
                >
                  {actionLoading ? (
                    <CircularProgress size={20} sx={{ color: isCheckedIn ? "#fff" : "#6C50BF" }} />
                  ) : (
                    <>
                      {isCheckedIn ? <LogoutOutlinedIcon sx={{ fontSize: 20 }} /> : <LoginOutlinedIcon sx={{ fontSize: 20 }} />}
                      {isCheckedIn ? "Check Out" : "Check In"}
                    </>
                  )}
                </Box>
              )}
              {!canCheckinOffice && (
                <Chip
                  icon={<LocationOnOutlinedIcon sx={{ color: "#FFD580 !important", fontSize: 15 }} />}
                  label="Off-site — reason required"
                  size="small"
                  sx={{ bgcolor: "rgba(249,188,6,0.15)", color: "#FFD580", border: "1px solid rgba(249,188,6,0.4)", fontWeight: 600 }}
                />
              )}
              {isCheckedIn && (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      color: "#FFD580",
                      borderColor: "rgba(249,188,6,0.4)",
                      bgcolor: onBreak ? "rgba(249,188,6,0.25)" : "transparent",
                      fontWeight: onBreak ? 700 : 500,
                    }}
                    onClick={async () => {
                      try {
                        const hasOpen = (todayRecord?.additionalPunches || []).find((p) => !p.punchOutAt);
                        if (hasOpen) {
                          await api.post("/api/attendance/punch-out");
                          toast.success("Break ended");
                        } else {
                          await api.post("/api/attendance/punch-in", { type: "break", label: "Break" });
                          toast.success("Break started");
                        }
                        loadData();
                      } catch (err) { toast.error(err.response?.data?.msg || "Action failed"); }
                    }}
                  >
                    {(todayRecord?.additionalPunches || []).find((p) => !p.punchOutAt) ? "End break" : "Start break"}
                  </Button>
                </Stack>
              )}
              {!isCheckedIn && !isDone && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.3)" }}
                  onClick={async () => {
                    try {
                      await api.post("/api/attendance/wfh", { reason: "Work from home" });
                      toast.success("Day marked as work-from-home");
                      loadData();
                    } catch (err) { toast.error(err.response?.data?.msg || "Action failed"); }
                  }}
                >
                  Mark WFH
                </Button>
              )}
            </Stack>

            {/* Today session details */}
            <Box sx={{ mt: 3.5, pt: 2.5, borderTop: "1px solid rgba(255,255,255,0.1)", position: "relative" }}>
              <Grid container spacing={2}>
                <SessionStat label="Check-in" value={todayRecord?.checkInTime ? fmtTime(todayRecord.checkInTime) : "—"} active={!!todayRecord?.checkInTime} />
                <SessionStat label="Check-out" value={todayRecord?.checkOutTime ? fmtTime(todayRecord.checkOutTime) : "—"} active={!!todayRecord?.checkOutTime} />
                <SessionStat
                  label="Expected end"
                  value={expectedEnd ? fmtTime(expectedEnd) : "—"}
                  sub={
                    todayRecord?.checkInTime
                      ? breakStats.totalBreakMs > 0
                        ? `${EXPECTED_NET_WORK_HOURS}h work + ${fmtDurFromMs(breakStats.totalBreakMs)} break`
                        : `${EXPECTED_NET_WORK_HOURS}h net work target`
                      : undefined
                  }
                />
                <SessionStat label="Today worked" value={fmtDurHM(workingMs)} highlight={isCheckedIn && !onBreak} />
                <SessionStat
                  label="Breaks today"
                  value={breakStats.count ? `${breakStats.count} · ${fmtDurFromMs(breakStats.totalBreakMs)}` : "0"}
                  active={onBreak}
                />
              </Grid>

              {/* Today's breaks */}
              {(isCheckedIn || isDone) && breakStats.count > 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: onBreak ? "rgba(249,188,6,0.12)" : "rgba(255,255,255,0.06)",
                    border: onBreak ? "1px solid rgba(249,188,6,0.35)" : "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <FreeBreakfastOutlinedIcon sx={{ fontSize: 18, color: onBreak ? "#FFD580" : "rgba(255,255,255,0.7)" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: onBreak ? "#FFD580" : "rgba(255,255,255,0.85)" }}>
                      Today&apos;s breaks ({breakStats.count})
                    </Typography>
                  </Stack>
                  <Stack spacing={0.75}>
                    {breakStats.breaks.map((b, idx) => (
                      <Stack
                        key={b.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                          bgcolor: b.isOpen ? "rgba(249,188,6,0.15)" : "transparent",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.85)" }}>
                          Break {idx + 1}: {fmtTime(b.start)}
                          {b.end ? ` – ${fmtTime(b.end)}` : " – now"}
                          {b.isOpen && (
                            <Box component="span" sx={{ ml: 0.75, color: "#FFD580", fontWeight: 700 }}>
                              (active)
                            </Box>
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#FFD580" }}>
                          {fmtDurFromMs(b.durationMs)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Shift progress bar */}
              <Box mt={2.5}>
                <Stack direction="row" justifyContent="space-between" mb={0.75}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: "rgba(255,255,255,0.55)" }}>
                    SHIFT PROGRESS
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.85)" }}>
                    {Math.round(shiftProgress * 100)}% · {String(Math.floor(SHIFT_START_HOUR)).padStart(2, "0")}:{String(Math.round((SHIFT_START_HOUR % 1) * 60)).padStart(2, "0")}–{String(SHIFT_END_HOUR).padStart(2, "0")}:00
                  </Typography>
                </Stack>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.1)", overflow: "hidden", position: "relative" }}>
                  <Box
                    sx={{
                      width: `${shiftProgress * 100}%`, height: "100%",
                      background: isCheckedIn
                        ? "linear-gradient(90deg, #39C645 0%, #8CE99A 100%)"
                        : "linear-gradient(90deg, #A684E8 0%, #E4D7FC 100%)",
                      transition: "width 0.8s ease",
                      boxShadow: isCheckedIn ? "0 0 12px rgba(57,198,69,0.6)" : "none",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* ============== Stats grid ============== */}
        <Grid item xs={12} md={5}>
          <Grid container spacing={2} sx={{ height: "100%" }}>
            <Grid item xs={6}>
              <MiniStat label="Present days" value={monthStats.present} hint="This month" accent="#39C645" icon={<CheckCircleOutlineIcon />} />
            </Grid>
            <Grid item xs={6}>
              <MiniStat label="Avg hrs/day" value={monthStats.avgHours.toFixed(1)} hint="This month" accent="#6C50BF" icon={<TrendingUpIcon />} />
            </Grid>
            <Grid item xs={6}>
              <MiniStat
                label="Punctuality"
                value={`${monthStats.punctualityPct}%`}
                hint={`${monthStats.late} late ${monthStats.late === 1 ? "day" : "days"}`}
                accent={monthStats.punctualityPct >= 90 ? "#39C645" : monthStats.punctualityPct >= 75 ? "#F9BC06" : "#EF4444"}
                icon={<AccessTimeOutlinedIcon />}
              />
            </Grid>
            <Grid item xs={6}>
              <MiniStat
                label="Streak"
                value={streak}
                hint={`${streak === 1 ? "day" : "days"} ${streak > 0 ? "in a row" : ""}`}
                accent="#F9BC06"
                icon={<EmojiEventsOutlinedIcon />}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ============== Week overview + insights ============== */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} md={7}>
          <SectionCard
            title="This Week"
            subtitle={`${fmtDurHM(weekData.totalMs)} of 40h target`}
            accent="#6C50BF"
          >
            <Box mb={2}>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>
                  Week progress
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: weekData.totalMs >= weekData.targetMs ? "#39C645" : "primary.main" }}>
                  {Math.round((weekData.totalMs / weekData.targetMs) * 100)}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min((weekData.totalMs / weekData.targetMs) * 100, 100)}
                sx={{
                  height: 8, borderRadius: 4,
                  "& .MuiLinearProgress-bar": {
                    bgcolor: weekData.totalMs >= weekData.targetMs ? "#39C645" : "#6C50BF",
                  },
                }}
              />
            </Box>

            {/* CSS grid with minmax(0,1fr) guarantees 7 equal columns that shrink
                to fit a phone — a Grid with `item xs` keeps min-content width and
                wraps the 7th day (Sunday) onto its own row. */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: { xs: 0.5, sm: 1 } }}>
              {weekData.days.map((d, i) => (
                <DayBar
                  key={i}
                  date={d.date}
                  hours={d.hours}
                  record={d.record}
                  isToday={d.date.toDateString() === now.toDateString()}
                  isFuture={d.date > now}
                  isOff={isCompanyOffDay(d.date, { saturdayPolicy: satPolicy, holidayByKey: holidayByDate })}
                />
              ))}
            </Box>
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={5}>
          <SectionCard title="Insights" accent="#F9BC06">
            <Stack spacing={1.75}>
              <InsightRow
                icon={<BoltOutlinedIcon />} color="#F9BC06"
                label="Overtime this month"
                value={`${monthStats.overtime.toFixed(1)}h`}
                hint={`Hours beyond ${EXPECTED_NET_WORK_HOURS}/day`}
              />
              <InsightRow
                icon={<LoginOutlinedIcon />} color="#6C50BF"
                label="Typical check-in"
                value={monthStats.earliestMin !== null ? `${String(Math.floor(monthStats.earliestMin / 60)).padStart(2, "0")}:${String(monthStats.earliestMin % 60).padStart(2, "0")}` : "—"}
                hint="Earliest this month"
              />
              <InsightRow
                icon={<FreeBreakfastOutlinedIcon />} color="#EF4444"
                label="Half days"
                value={monthStats.halfDays}
                hint="Less than 4h worked"
              />
              {nextHoliday && (
                <InsightRow
                  icon={<EventAvailableOutlinedIcon />} color="#39C645"
                  label="Next holiday"
                  value={new Date(nextHoliday.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  hint={nextHoliday.name}
                />
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5, mb: 1 }}>
              Quick actions
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<BeachAccessOutlinedIcon />} onClick={() => nav("/ApplyForLeave")}>
                Apply leave
              </Button>
              <Button size="small" variant="outlined" startIcon={<AccessTimeOutlinedIcon />} onClick={() => nav("/overtime")}>
                Log OT
              </Button>
              <Button size="small" variant="outlined" startIcon={<EditCalendarOutlinedIcon />} onClick={() => nav("/AttendanceRegularization")}>
                Fix attendance
              </Button>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* ============== 14-day sparkline + Recent entries ============== */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Last 14 days" subtitle="Daily hours worked" accent="#5C85FF">
            <Sparkline data={sparkline} target={EXPECTED_NET_WORK_HOURS} />
          </SectionCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionCard title="Recent check-ins" subtitle="Latest activity" accent="#39C645">
            {loading ? (
              <LinearProgress sx={{ borderRadius: 1 }} />
            ) : records.length === 0 ? (
              <EmptyState title="No records yet" subtitle="Your first check-in will appear here." />
            ) : (
              <Stack spacing={1}>
                {records.slice(0, 8).map((r) => <RecentRow key={r._id} record={r} />)}
                {records.length > 8 && (
                  <Button size="small" onClick={() => nav("/GetMyAttendance")}>
                    View all {records.length} records →
                  </Button>
                )}
              </Stack>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Reason dialog for off-site */}
      <Dialog open={!!reasonOpen} onClose={() => setReasonOpen(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LocationOnOutlinedIcon color="warning" />
            {reasonOpen === "checkin" ? "Off-site Check In" : "Off-site Check Out"}
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              You're not on the office network. Provide a reason so your manager can approve this entry.
            </Typography>
            <TextField fullWidth label="Reason *" value={reason} onChange={(e) => setReason(e.target.value)} multiline rows={2} placeholder="Client meeting, work from home, etc." />
            <TextField
              fullWidth
              label="Location *"
              value={locationLoading ? "Detecting your location…" : location}
              disabled={locationLoading}
              error={Boolean(locationError)}
              inputProps={{ readOnly: true }}
              helperText={
                locationLoading
                  ? "Using GPS and reverse geocoding"
                  : locationError || "Detected from your device — location must be on"
              }
              InputProps={{
                endAdornment: locationLoading ? <CircularProgress size={18} /> : null,
              }}
            />
            {locationError && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="error.main">
                  {locationError}
                </Typography>
                <Button size="small" onClick={fetchOutsideLocation} disabled={locationLoading || actionLoading}>
                  Retry location
                </Button>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonOpen(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!reason?.trim() || locationLoading || actionLoading || !geoCoords || !location.trim()}
            onClick={() => (reasonOpen === "checkin" ? doCheckin(reason, location) : doCheckout(reason, location))}
          >
            {reasonOpen === "checkin" ? "Check In" : "Check Out"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reason dialog for early check-out before 8h */}
      <Dialog
        open={earlyLeaveReasonOpen}
        onClose={() => setEarlyLeaveReasonOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberOutlinedIcon color="warning" />
            Early Check Out
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              You are checking out before {EARLY_LEAVE_REASON_HOURS} hours. Please provide a reason for early leave.
            </Typography>
            <TextField
              fullWidth
              label="Early leave reason *"
              value={earlyLeaveReason}
              onChange={(e) => setEarlyLeaveReason(e.target.value)}
              multiline
              rows={3}
              placeholder="Doctor appointment, urgent personal work, etc."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEarlyLeaveReasonOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!earlyLeaveReason?.trim() || actionLoading}
            onClick={() => doCheckout(undefined, undefined, earlyLeaveReason)}
          >
            Check Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ---------------- mini components ---------------- */

function StatusPill({ label, color, pulse }) {
  return (
    <Stack
      direction="row" alignItems="center" spacing={0.75}
      sx={{
        px: 1.25, py: 0.5, borderRadius: 10,
        bgcolor: `${color}25`, border: `1px solid ${color}50`,
      }}
    >
      <Box sx={{ position: "relative", width: 8, height: 8 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
        {pulse && (
          <Box
            sx={{
              position: "absolute", inset: 0, borderRadius: "50%", bgcolor: color,
              animation: "pulseRing 1.6s ease-out infinite",
              "@keyframes pulseRing": {
                "0%": { transform: "scale(1)", opacity: 0.6 },
                "70%": { transform: "scale(2.2)", opacity: 0 },
                "100%": { opacity: 0 },
              },
            }}
          />
        )}
      </Box>
      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color }}>
        {label}
      </Typography>
    </Stack>
  );
}

function SessionStat({ label, value, sub, active, highlight }) {
  return (
    <Grid item xs={6} sm={3}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "rgba(255,255,255,0.5)", textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 20, fontWeight: 700, mt: 0.25,
          color: highlight ? "#8CE99A" : active ? "#fff" : "rgba(255,255,255,0.4)",
          fontFeatureSettings: '"tnum"',
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.45)", mt: 0.25, lineHeight: 1.3 }}>
          {sub}
        </Typography>
      )}
    </Grid>
  );
}

function MiniStat({ label, value, hint, accent, icon }) {
  return (
    <Box
      sx={{
        p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider",
        bgcolor: "background.paper", height: "100%", position: "relative",
        borderBottom: `3px solid ${accent}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Box sx={{ color: accent, display: "flex", alignItems: "center" }}>
          {icon}
        </Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color: accent }}>{value}</Typography>
      {hint && <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>{hint}</Typography>}
    </Box>
  );
}

function DayBar({ date, hours, record, isToday, isFuture, isOff }) {
  const weekend = isOff != null ? isOff : (date.getDay() === 0 || date.getDay() === 6);
  const target = EXPECTED_NET_WORK_HOURS;
  const pct = Math.min((hours / target) * 100, 100);
  const label = date.toLocaleDateString("en-IN", { weekday: "short" });
  const getColor = () => {
    if (isFuture) return "#E5E7EB";
    if (weekend) return "#D1D5DB";
    if (hours === 0) return "#FEE2E2";
    if (hours < 4) return "#FEF3C7";
    if (hours < target) return "#BFDBFE";
    return "#86EFAC";
  };
  const fillColor = hours >= target ? "#39C645" : hours >= 4 ? "#6C50BF" : hours > 0 ? "#F9BC06" : "#9CA3AF";

  return (
    <Tooltip
      title={
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
            {date.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" })}
          </Typography>
          {record?.checkInTime ? (
            <>
              <Typography sx={{ fontSize: 11 }}>In: {fmtTime(record.checkInTime)}</Typography>
              <Typography sx={{ fontSize: 11 }}>Out: {record.checkOutTime ? fmtTime(record.checkOutTime) : "In progress"}</Typography>
              <Typography sx={{ fontSize: 11 }}>Worked: {hours.toFixed(1)}h</Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 11 }}>{weekend ? "Weekly Off" : isFuture ? "Upcoming" : "No record"}</Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          p: { xs: 0.5, sm: 1 }, minWidth: 0, borderRadius: 2, textAlign: "center",
          bgcolor: isToday ? "#F4EDFA" : "transparent",
          border: isToday ? "2px solid" : "1px solid", borderColor: isToday ? "primary.main" : "divider",
          cursor: "default", transition: "all 0.15s",
          "&:hover": { transform: "translateY(-2px)" },
        }}
      >
        <Typography sx={{ fontSize: { xs: 9, sm: 10 }, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.3 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 700, mt: 0.25 }}>
          {date.getDate()}
        </Typography>
        <Box sx={{ mt: { xs: 0.5, sm: 1 }, height: { xs: 44, sm: 60 }, display: "flex", alignItems: "flex-end", justifyContent: "center", position: "relative" }}>
          <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, top: 0, bgcolor: getColor(), borderRadius: 1, opacity: 0.3 }} />
          {!isFuture && !weekend && hours > 0 && (
            <Box sx={{
              width: "70%", height: `${pct}%`, minHeight: 3,
              bgcolor: fillColor, borderRadius: 1, position: "relative", zIndex: 1,
              transition: "height 0.4s",
            }} />
          )}
        </Box>
        <Typography sx={{ fontSize: { xs: 9, sm: 10 }, fontWeight: 700, mt: 0.5, color: isFuture || weekend ? "text.secondary" : "text.primary" }}>
          {isFuture ? "—" : weekend ? "WE" : hours > 0 ? `${hours.toFixed(1)}h` : "—"}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function InsightRow({ icon, color, label, value, hint }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 36, height: 36, borderRadius: 2, flexShrink: 0,
          bgcolor: `${color}15`, color, display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{hint}</Typography>
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color }}>{value}</Typography>
    </Stack>
  );
}

function Sparkline({ data, target }) {
  const width = 280;
  const height = 100;
  const padding = 8;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;
  const max = Math.max(target, ...data.map((d) => d.hours), 1);
  const barW = innerW / data.length - 2;
  const avg = data.reduce((s, d) => s + d.hours, 0) / (data.filter((d) => d.hours > 0).length || 1);

  return (
    <Box>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMinYMid meet" style={{ width: "100%", minWidth: width }}>
          {/* Target line */}
          <line
            x1={padding} y1={height - padding - (target / max) * innerH}
            x2={width - padding} y2={height - padding - (target / max) * innerH}
            stroke="#6C50BF" strokeWidth="1" strokeDasharray="3,3" opacity="0.5"
          />
          <text x={width - padding - 20} y={height - padding - (target / max) * innerH - 4} fontSize="9" fill="#6C50BF" fontWeight="700">
            {target}h
          </text>

          {data.map((d, i) => {
            const x = padding + i * (innerW / data.length);
            const barH = (d.hours / max) * innerH;
            const y = height - padding - barH;
            const isWeekend = d.date.getDay() === 0 || d.date.getDay() === 6;
            const color = d.hours === 0 ? "#E5E7EB" : d.hours >= target ? "#39C645" : d.hours >= target / 2 ? "#6C50BF" : "#F9BC06";
            return (
              <g key={i}>
                <rect
                  x={x + 1} y={y} width={barW} height={barH}
                  fill={isWeekend ? "#D1D5DB" : color}
                  rx={2}
                  opacity={isWeekend ? 0.5 : 1}
                />
                {d.date.toDateString() === new Date().toDateString() && (
                  <rect x={x + 1} y={y - 2} width={barW} height={2} fill="#EF4444" />
                )}
              </g>
            );
          })}
        </svg>
      </Box>
      <Stack direction="row" justifyContent="space-between" mt={1}>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {data[0]?.date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "primary.main" }}>
          Avg {avg.toFixed(1)}h/day
        </Typography>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {data[data.length - 1]?.date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
        </Typography>
      </Stack>
    </Box>
  );
}

function RecentRow({ record }) {
  const d = new Date(record.checkInTime || record.createdAt);
  const hours = hoursOf(record.workingHours);
  const outside = record.checkInSource === "outside" || record.checkOutSource === "outside";
  const ci = new Date(record.checkInTime);
  const ciMin = ci.getHours() * 60 + ci.getMinutes();
  const late = ciMin > SHIFT_START_HOUR * 60 + 15;
  const isToday = d.toDateString() === new Date().toDateString();
  const statusInfo = hours >= EXPECTED_NET_WORK_HOURS ? { label: "Full day", color: "#39C645", bg: "#EDFAF2" }
    : hours >= 4 ? { label: "Half day", color: "#F9BC06", bg: "#FEF3C7" }
    : hours > 0 ? { label: "Short", color: "#EF4444", bg: "#FEE2E2" }
    : { label: "No hours", color: "#9CA3AF", bg: "#F3F4F6" };

  return (
    <Stack
      direction="row" spacing={2} alignItems="center"
      sx={{
        p: 1.5, border: "1px solid", borderColor: isToday ? "primary.main" : "divider",
        borderRadius: 2, bgcolor: isToday ? "#F4EDFA" : "background.paper",
        transition: "all 0.15s",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Box sx={{ textAlign: "center", minWidth: 44 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: isToday ? "primary.main" : "text.primary" }}>
          {d.getDate()}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "text.secondary", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.3 }}>
          {d.toLocaleDateString("en-IN", { month: "short" })}
        </Typography>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.25} flexWrap="wrap" useFlexGap>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {fmtTime(record.checkInTime)} → {record.checkOutTime ? fmtTime(record.checkOutTime) : "running"}
          </Typography>
          {late && <Chip label="Late" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#FEE2E2", color: "#EF4444", fontWeight: 700 }} />}
          {outside && <Chip label="Off-site" size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#FEF3C7", color: "#92400E", fontWeight: 700 }} />}
        </Stack>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          <b>{hours.toFixed(1)}h</b> worked
          {d.toLocaleDateString("en-IN", { weekday: "long" }) && ` • ${d.toLocaleDateString("en-IN", { weekday: "long" })}`}
          {record.checkInReason ? ` • ${record.checkInReason}` : ""}
        </Typography>
      </Box>
      <Chip label={statusInfo.label} size="small" sx={{ bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 700, fontSize: 11 }} />
    </Stack>
  );
}
